import { useEffect } from "react";

import useSignOut from "./useSignOut";
import { useAppSelector } from "../store/hooks";

/** Twenty minutes of no mouse or keyboard activity. */
const IDLE_MS = 20 * 60 * 1000;

/**
 * Signs a person out after a period of inactivity.
 *
 * Only when there is a session to end: the previous implementation armed the
 * timer unconditionally and, on firing, cleared the whole of localStorage and
 * sessionStorage for every visitor, signed in or not.
 *
 * The listeners are passive and on the document, and the timer is reset rather
 * than replaced, so this costs a comparison per event rather than a timer
 * churn.
 */
export function useIdleSignOut(): void {
    const signedIn = useAppSelector((state) => Boolean(state.auth.accessToken));
    const signOut = useSignOut();

    useEffect(() => {
        if (!signedIn) return;

        let timeout = window.setTimeout(() => void signOut(), IDLE_MS);

        const reset = () => {
            window.clearTimeout(timeout);
            timeout = window.setTimeout(() => void signOut(), IDLE_MS);
        };

        const options: AddEventListenerOptions = { passive: true };
        document.addEventListener("mousemove", reset, options);
        document.addEventListener("keydown", reset, options);
        document.addEventListener("touchstart", reset, options);

        return () => {
            window.clearTimeout(timeout);
            document.removeEventListener("mousemove", reset, options);
            document.removeEventListener("keydown", reset, options);
            document.removeEventListener("touchstart", reset, options);
        };
    }, [signedIn, signOut]);
}

export default useIdleSignOut;
